import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
    Alert, alpha, Autocomplete, Avatar, Box, Button, Chip, CircularProgress,
    Collapse, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton,
    InputAdornment, List, ListItemButton, ListItemIcon, ListItemText,
    MenuItem, Paper, Switch, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Tooltip, Typography, useTheme
} from '@mui/material';

import {
    Add as AddIcon, ArrowForward as ArrowForwardIcon, Check as CheckIcon,
    Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon,
    InfoOutlined as InfoIcon, Label as LabelIcon, MoreVert as MoreVertIcon,
    People as PeopleIcon, PriorityHigh as PriorityIcon, PublicOff as PrivateIcon,
    Public as PublicIcon, Save as SaveIcon, Search as SearchIcon,
    SortByAlpha as SortIcon
} from '@mui/icons-material';
import SettingsIcon from '@mui/icons-material/Settings';
import api from '../../services/api';
import { MuiColorInput } from 'mui-color-input';

// Mock data (would be replaced with real data in production)
const FAKE_USERS = [
    { id: 1, name: 'João Silva', avatar: null, color: '#4F3CC9', role: 'admin' },
    { id: 2, name: 'Maria Oliveira', avatar: 'https://i.pravatar.cc/150?img=5', color: null, role: 'editor' },
    // More users...
];

const ALL_USERS = [
    ...FAKE_USERS,
    { id: 6, name: 'Fernanda Lima', avatar: 'https://i.pravatar.cc/150?img=10', color: null, role: null },
    // More users...
];

// Configuration constants
const DISPLAY_SETTINGS_CONFIG = [
    {
        key: "showTaskCount",
        label: "Exibir Contagem de Tarefas",
        description: "Mostra o número de tarefas em cada coluna"
    },
    {
        key: "showTags",
        label: "Exibir Marcadores",
        description: "Mostra os marcadores coloridos nos cards"
    },
    {
        key: "showPriorityName",
        label: "Exibir Nome da Prioridade",
        description: "Mostra o texto da prioridade além da cor"
    }
];

const INFO_SETTINGS_CONFIG = [
    {
        key: "showTicketNumber",
        label: "Exibir Número do Chamado",
        description: "Mostra o número de identificação de cada tarefa"
    },
    {
        key: "showCardAuthorship",
        label: "Exibir Autoria nos Cards",
        description: "Mostra quem criou a tarefa"
    }
];

const DRAWER_WIDTH = 220;

// Styled components and common styles
const StyledTableContainer = ({ children }) => {
    const theme = useTheme();

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
                borderRadius: 1.5,
                boxShadow: 'none',
                '& .MuiTableCell-root': {
                    padding: '10px 16px',
                    borderColor: theme.palette.divider
                },
                overflow: 'hidden',
                border: '1px solid',
                borderColor: theme.palette.divider
            }}
        >
            {children}
        </TableContainer>
    );
};

const ActionButton = ({ icon: Icon, onClick, tooltip, disabled, color = "primary", ariaLabel }) => {
    const theme = useTheme();

    return (
        <Tooltip title={tooltip}>
            <span>
                <IconButton
                    size="small"
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={ariaLabel}
                    sx={{
                        color: theme.palette[color].main,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette[color].main, 0.08)
                        },
                        padding: 0.75,
                    }}
                >
                    <Icon fontSize="small" sx={{ fontSize: 18 }} />
                </IconButton>
            </span>
        </Tooltip>
    );
};

// Reusable components
const SearchFilterBar = ({ searchQuery, setSearchQuery, sortConfig, handleSort, filterGlobal, handleFilterGlobalChange, showGlobalFilter = true }) => {
    const theme = useTheme();

    return (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
                placeholder="Pesquisar..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                        <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                    sx: { borderRadius: 1.5 }
                }}
                sx={{ flexGrow: 1, minWidth: '200px' }}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Ordenar por nome">
                    <IconButton
                        size="small"
                        onClick={handleSort}
                        sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            color: sortConfig.direction === 'asc' ? theme.palette.primary.main : theme.palette.text.secondary,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.08)
                            }
                        }}
                    >
                        <SortIcon
                            fontSize="small"
                            sx={{
                                transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s ease'
                            }}
                        />
                    </IconButton>
                </Tooltip>

                {showGlobalFilter && (
                    <Box sx={{
                        display: 'flex',
                        borderRadius: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden',
                        height: 32
                    }}>
                        {['all', 'global', 'local'].map((filter) => (
                            <Button
                                key={filter}
                                size="small"
                                disableElevation
                                onClick={() => handleFilterGlobalChange(filter)}
                                sx={{
                                    borderRadius: 0,
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    textTransform: 'none',
                                    color: filterGlobal === filter ? theme.palette.primary.main : theme.palette.text.secondary,
                                    bgcolor: filterGlobal === filter ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                    borderRight: filter !== 'local' ? `1px solid ${theme.palette.divider}` : 'none',
                                    '&:hover': {
                                        bgcolor: filterGlobal === filter
                                            ? alpha(theme.palette.primary.main, 0.12)
                                            : alpha(theme.palette.action.hover, 0.08)
                                    }
                                }}
                            >
                                {filter === 'all' ? 'Todos' : filter === 'global' ? 'Globais' : 'Locais'}
                            </Button>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const EmptyState = ({ icon: IconComponent, message, subMessage, buttonText, onButtonClick }) => {
    const theme = useTheme();

    return (
        <Box sx={{
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.action.hover, 0.05),
            borderRadius: 2,
            border: `1px dashed ${theme.palette.divider}`
        }}>
            <IconComponent sx={{ fontSize: 48, color: alpha(theme.palette.text.secondary, 0.2), mb: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {message}
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center" sx={{ mb: 2, maxWidth: 300 }}>
                {subMessage}
            </Typography>
            <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={onButtonClick}
                sx={{
                    mt: 1,
                    textTransform: 'none',
                    borderRadius: 1.5,
                    fontWeight: 500
                }}
            >
                {buttonText}
            </Button>
        </Box>
    );
};

const ScopeSelector = ({ value, onChange, labelGlobal, labelLocal, descGlobal, descLocal }) => {
    const theme = useTheme();

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                Escopo
            </Typography>

            <Box sx={{
                display: 'flex',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1.5,
                overflow: 'hidden'
            }}>
                {/* Global Option */}
                <Box
                    sx={{
                        flex: 1,
                        p: 2,
                        cursor: 'pointer',
                        bgcolor: value ? alpha(theme.palette.info.main, 0.08) : 'transparent',
                        borderRight: `1px solid ${theme.palette.divider}`,
                        transition: 'background-color 0.2s',
                        '&:hover': {
                            bgcolor: value
                                ? alpha(theme.palette.info.main, 0.12)
                                : alpha(theme.palette.action.hover, 0.08)
                        }
                    }}
                    onClick={() => onChange(true)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            mr: 1,
                            bgcolor: value ? 'info.main' : alpha(theme.palette.action.hover, 0.4)
                        }}>
                            {value && <CheckIcon sx={{ color: 'white', fontSize: 14 }} />}
                        </Box>
                        <Typography
                            variant="body2"
                            color={value ? 'info.main' : 'text.primary'}
                            fontWeight={value ? 600 : 500}
                        >
                            {labelGlobal}
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3.5 }}>
                        {descGlobal}
                    </Typography>
                </Box>

                {/* Local Option */}
                <Box
                    sx={{
                        flex: 1,
                        p: 2,
                        cursor: 'pointer',
                        bgcolor: !value ? alpha(theme.palette.warning.main, 0.08) : 'transparent',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                            bgcolor: !value
                                ? alpha(theme.palette.warning.main, 0.12)
                                : alpha(theme.palette.action.hover, 0.08)
                        }
                    }}
                    onClick={() => onChange(false)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            mr: 1,
                            bgcolor: !value ? 'warning.main' : alpha(theme.palette.action.hover, 0.4)
                        }}>
                            {!value && <CheckIcon sx={{ color: 'white', fontSize: 14 }} />}
                        </Box>
                        <Typography
                            variant="body2"
                            color={!value ? 'warning.main' : 'text.primary'}
                            fontWeight={!value ? 600 : 500}
                        >
                            {labelLocal}
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3.5 }}>
                        {descLocal}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

// Main component
const CreateOrEditKanbanSettingsModal = ({
    open,
    onClose,
    tags: realTags,
    assigneds: realAssigneds,
    priorities: realPriorities,
    displayFilters,
    openIn = 'display',
    setDisplayFilters,
    onDisplayChange,
    kanbanId // Added kanbanId prop
}) => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState('display');
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState({
        type: null,
        id: null
    });
    const [tags, setTags] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [users, setUsers] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    // Fix: Initialize with a default color instead of null
    const [colorPick, setColor] = useState('#6C757D');
    const [searchQuery, setSearchQuery] = useState('');
    const [showHelp, setShowHelp] = useState(true);
    const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });
    const [filterGlobal, setFilterGlobal] = useState('all');
    const [addingUser, setAddingUser] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userRole, setUserRole] = useState('editor');
    const [searchUserQuery, setSearchUserQuery] = useState('');
    const [hasErrors, setHasErrors] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const greetingRef = useRef();

    // Effect hooks for initializing data
    useEffect(() => {
        setPriorities(realPriorities || []);
        setTags(realTags || []);
        setUsers(realAssigneds || []);
        setActiveTab(openIn || 'display');
    }, [realPriorities, realTags, realAssigneds, openIn]);

    // Fix: Add an effect to update colorPick when editingItem changes
    useEffect(() => {
        if (editingItem && editingItem.color) {
            setColor(editingItem.color);
        } else {
            // Reset to default when no item is being edited
            setColor('#6C757D');
        }
    }, [editingItem]);

    useEffect(() => {
        if (showHelp) {
            const timer = setTimeout(() => setShowHelp(false), 8000);
            return () => clearTimeout(timer);
        }
    }, [showHelp]);

    // Display settings handlers
    const handleDisplaySettingChange = (setting) => (event) => {
        const newValue = event.target.checked;
        setDisplayFilters(prevSettings => ({
            ...prevSettings,
            [setting]: newValue
        }));

        if (typeof onDisplayChange === 'function') {
            onDisplayChange({ ...displayFilters, [setting]: newValue });
        }
    };

    // User filtering
    const filteredAllUsers = useMemo(() => {
        const alreadyAddedUserIds = users.map(user => user.id);
        return ALL_USERS
            .filter(user => !alreadyAddedUserIds.includes(user.id))
            .filter(user =>
                searchUserQuery === '' ||
                user.name.toLowerCase().includes(searchUserQuery.toLowerCase()) ||
                String(user.id).includes(searchUserQuery)
            );
    }, [users, searchUserQuery]);

    const filteredUsers = useMemo(() => {
        return users
            .map(user => ({
                ...user,
                role: user.KanbanUser?.role || 'admin'
            }))
            .filter(user =>
                searchQuery === '' ||
                user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                String(user.id).includes(searchQuery)
            )
            .sort((a, b) => {
                if (sortConfig.field === 'name') {
                    return sortConfig.direction === 'asc'
                        ? a.name.localeCompare(b.name)
                        : b.name.localeCompare(a.name);
                }
                return 0;
            });
    }, [users, searchQuery, sortConfig]);

    // Filter functions for tags and priorities
    const applyFilters = useCallback((items) => {
        return items
            .filter(item => {
                // Search filter
                const matchesSearch = searchQuery === '' ||
                    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.color.toLowerCase().includes(searchQuery.toLowerCase());

                // Global/local scope filter
                const matchesGlobalFilter =
                    filterGlobal === 'all' ||
                    (filterGlobal === 'global' && item.isGlobal) ||
                    (filterGlobal === 'local' && !item.isGlobal);

                return matchesSearch && matchesGlobalFilter;
            })
            .sort((a, b) => {
                if (sortConfig.field === 'title' || sortConfig.field === 'name') {
                    return sortConfig.direction === 'asc'
                        ? a.title.localeCompare(b.title)
                        : b.title.localeCompare(a.title);
                }
                return 0;
            });
    }, [searchQuery, filterGlobal, sortConfig]);

    const filteredTags = useMemo(() => applyFilters(tags), [tags, applyFilters]);
    const filteredPriorities = useMemo(() => applyFilters(priorities), [priorities, applyFilters]);

    const handleSort = () => {
        setSortConfig({
            field: 'name',
            direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
        });
    };

    const handleFilterGlobalChange = (value) => setFilterGlobal(value);

    // Error handling
    const showError = (message) => {
        setHasErrors(true);
        setErrorMessage(message);
        setTimeout(() => {
            setHasErrors(false);
            setErrorMessage('');
        }, 5000);
    };

    // Edit handlers
    const handleEdit = (item, itemType) => {
        if (!item) return;
        // Fix: Set the color state right away
        setColor(item.color);
        setEditingItem({ ...item, type: itemType });
    };

    const handleCancelEdit = () => setEditingItem(null);

    const handleSaveEdit = async (e) => {
        e.stopPropagation();

        if (!editingItem) return;

        try {
            setActionLoading({ type: 'edit', id: editingItem.id });

            if (editingItem.type === 'tag') {
                await api.put(`/kanban/tag/${editingItem.id}`, editingItem);
                setTags(tags.map(tag => tag.id === editingItem.id ? editingItem : tag));
            } else if (editingItem.type === 'priority') {
                await api.put(`/kanban/priority/${editingItem.id}`, editingItem);
                setPriorities(priorities.map(priority => priority.id === editingItem.id ? editingItem : priority));
            }

            setEditingItem(null);
        } catch (error) {
            console.error(`Error updating ${editingItem.type}:`, error);
            showError(`Erro ao salvar: ${error.message || 'Verifique sua conexão'}`);
        } finally {
            setActionLoading({ type: null, id: null });
        }
    };

    // Delete handlers
    const handleDelete = async (id, itemType) => {
        try {
            setActionLoading({ type: 'delete', id });

            if (itemType === 'tag') {
                await api.delete(`/kanban/tag/${id}`);
                setTags(tags.filter(tag => tag.id !== id));
            } else if (itemType === 'priority') {
                await api.delete(`/kanban/priority/${id}`);
                setPriorities(priorities.filter(priority => priority.id !== id));
            }
        } catch (error) {
            console.error(`Error deleting ${itemType}:`, error);
            showError(`Erro ao excluir: ${error.message || 'Verifique sua conexão'}`);
        } finally {
            setActionLoading({ type: null, id: null });
        }
    };

    // Add new item handlers
    const handleAddNew = (itemType) => {
        const newId = itemType === 'tag'
            ? Math.max(0, ...tags.map(t => t.id)) + 1
            : Math.max(0, ...priorities.map(p => p.id)) + 1;

        const defaultColor = itemType === 'tag' ? '#6C757D' : '#DC3545';
        // Fix: Set the color state right away
        setColor(defaultColor);

        const newItem = {
            id: newId,
            title: '',
            color: defaultColor,
            isGlobal: false,
            type: itemType
        };

        setEditingItem(newItem);
    };

    const handleSaveNew = async (e) => {
        e.stopPropagation();

        if (!editingItem || !editingItem.title.trim()) return;

        try {
            setActionLoading({ type: 'add', id: editingItem.id });

            if (editingItem.type === 'tag') {
                const tagData = {
                    title: editingItem.title,
                    color: editingItem.color,
                    isGlobal: editingItem.isGlobal
                };

                const { data } = await api.post(`/kanban/tag/${kanbanId}`, tagData);
                // Use the returned data that includes the server-generated ID
                setTags([...tags, data || { ...tagData, id: editingItem.id }]);
            } else if (editingItem.type === 'priority') {
                const priorityData = {
                    title: editingItem.title,
                    color: editingItem.color,
                    isGlobal: editingItem.isGlobal
                };

                const { data } = await api.post(`/kanban/priority/${kanbanId}`, priorityData);
                // Use the returned data that includes the server-generated ID
                setPriorities([...priorities, data || { ...priorityData, id: editingItem.id }]);
            }

            setEditingItem(null);
        } catch (error) {
            console.error(`Error creating new ${editingItem.type}:`, error);
            showError(`Erro ao criar: ${error.message || 'Verifique sua conexão'}`);
        } finally {
            setActionLoading({ type: null, id: null });
        }
    };

    // Fix: Update the handleColorChange function to also update the editingItem
    const handleColorChange = (color) => {
        setColor(color);
        if (editingItem) {
            setEditingItem({ ...editingItem, color });
        }
    };

    // User management handlers
    const handleOpenAddUser = () => {
        setAddingUser(true);
        setSelectedUser(null);
        setUserRole('editor');
        setSearchUserQuery('');
    };

    const handleCloseAddUser = () => setAddingUser(false);

    const handleAddUser = async () => {
        if (!selectedUser) return;

        try {
            setLoading(true);
            // Add API call to add the user to the kanban
            // await api.post(`/kanban/user/${kanbanId}`, { userId: selectedUser.id, role: userRole });

            const newUser = { ...selectedUser, role: userRole };
            setUsers([...users, newUser]);
            setAddingUser(false);
        } catch (error) {
            console.error('Error adding user:', error);
            showError(`Erro ao adicionar usuário: ${error.message || 'Verifique sua conexão'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleChangeUserRole = async (userId, newRole) => {
        try {
            setActionLoading({ type: 'role', id: userId });
            // API call to update the user role
            // await api.put(`/kanban/user/${kanbanId}/${userId}`, { role: newRole });

            setUsers(users.map(user =>
                user.id === userId ? { ...user, role: newRole } : user
            ));
        } catch (error) {
            console.error('Error changing user role:', error);
            showError(`Erro ao alterar função: ${error.message || 'Verifique sua conexão'}`);
        } finally {
            setActionLoading({ type: null, id: null });
        }
    };

    const handleRemoveUser = async (userId) => {
        try {
            setActionLoading({ type: 'removeUser', id: userId });
            // API call to remove the user
            // await api.delete(`/kanban/user/${kanbanId}/${userId}`);

            setUsers(users.filter(user => user.id !== userId));
        } catch (error) {
            console.error('Error removing user:', error);
            showError(`Erro ao remover usuário: ${error.message || 'Verifique sua conexão'}`);
        } finally {
            setActionLoading({ type: null, id: null });
        }
    };

    // Generic DataTable component with better error handling
    const DataTable = ({ items, itemType, columns, renderRow, emptyStateProps, isLoading }) => {
        const theme = useTheme();

        if (isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress size={30} thickness={4} />
                </Box>
            );
        }

        if (items.length === 0) {
            return <EmptyState {...emptyStateProps} />;
        }

        return (
            <StyledTableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                            {columns.map((col, index) => (
                                <TableCell
                                    key={index}
                                    align={col.align || 'left'}
                                    width={col.width}
                                >
                                    <Typography variant="subtitle2" fontWeight={500}>{col.title}</Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Header for global items */}
                        {(filterGlobal === 'global' || filterGlobal === 'all') &&
                            items.some(item => item.isGlobal) &&
                            filterGlobal === 'all' && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} sx={{ bgcolor: alpha(theme.palette.info.main, 0.04), py: 1 }}>
                                        <Typography variant="caption" fontWeight={600} color="info.main" sx={{ display: 'flex', alignItems: 'center' }}>
                                            <PublicIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                                            {itemType === 'tag' ? 'MARCADORES GLOBAIS' : 'PRIORIDADES GLOBAIS'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                        {/* Global items */}
                        {(filterGlobal === 'global' || filterGlobal === 'all') &&
                            items.filter(item => item.isGlobal).map(renderRow)}

                        {/* Header for local items */}
                        {(filterGlobal === 'local' || filterGlobal === 'all') &&
                            items.some(item => !item.isGlobal) &&
                            filterGlobal === 'all' && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} sx={{ bgcolor: alpha(theme.palette.warning.main, 0.04), py: 1 }}>
                                        <Typography variant="caption" fontWeight={600} color="warning.main" sx={{ display: 'flex', alignItems: 'center' }}>
                                            <PrivateIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                                            {itemType === 'tag' ? 'MARCADORES LOCAIS' : 'PRIORIDADES LOCAIS'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                        {/* Local items */}
                        {(filterGlobal === 'local' || filterGlobal === 'all') &&
                            items.filter(item => !item.isGlobal).map(renderRow)}
                    </TableBody>
                </Table>
            </StyledTableContainer>
        );
    };

    // Tab content components
    const DisplaySettingsContent = () => (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={500} gutterBottom>
                Configurações de Exibição
            </Typography>

            <Collapse in={showHelp}>
                <Alert
                    severity="info"
                    sx={{ mb: 3, borderRadius: 1.5 }}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setShowHelp(false)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    <Typography variant="body2">
                        Personalize a visualização dos seus cards no kanban ajustando as opções abaixo.
                    </Typography>
                </Alert>
            </Collapse>

            {/* Visual Elements */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 'none', mb: 3 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <SettingsIcon sx={{ mr: 1, fontSize: 18 }} />
                    Elementos Visuais
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {DISPLAY_SETTINGS_CONFIG.map((setting) => {
                        const isChecked = displayFilters[setting.key];
                        return (
                            <Box key={setting.key} sx={{
                                p: 1.5,
                                borderRadius: 1.5,
                                bgcolor: isChecked ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                transition: 'background-color 0.2s ease',
                                '&:hover': {
                                    bgcolor: isChecked ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.action.hover, 0.1)
                                }
                            }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isChecked}
                                            onChange={handleDisplaySettingChange(setting.key)}
                                            disabled={loading}
                                            color="primary"
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={500} color={isChecked ? "primary" : "text.primary"}>
                                                {setting.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {setting.description}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ margin: 0, width: '100%' }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Paper>

            {/* Card Information */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 'none' }}>
                <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ mr: 1, fontSize: 18 }} />
                    Informações nos Cards
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {INFO_SETTINGS_CONFIG.map((setting) => {
                        const isChecked = displayFilters[setting.key];
                        return (
                            <Box key={setting.key} sx={{
                                p: 1.5,
                                borderRadius: 1.5,
                                bgcolor: isChecked ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                transition: 'background-color 0.2s ease',
                                '&:hover': {
                                    bgcolor: isChecked ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.action.hover, 0.1)
                                }
                            }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isChecked}
                                            onChange={handleDisplaySettingChange(setting.key)}
                                            disabled={loading}
                                            color="primary"
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={500} color={isChecked ? "primary" : "text.primary"}>
                                                {setting.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {setting.description}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ margin: 0, width: '100%' }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Paper>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <CircularProgress size={24} thickness={4} />
                </Box>
            )}
        </Box>
    );

    // Reusable component for Tag/Priority row
    const ItemTableRow = ({ item, itemType }) => {
        const isLoading = actionLoading.type && actionLoading.id === item.id;

        return (
            <TableRow key={item.id} hover>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                            sx={{
                                width: 14,
                                height: 14,
                                bgcolor: item.color,
                                borderRadius: '3px',
                                mr: 1.5
                            }}
                        />
                        <Typography variant="body2" fontWeight={500}>{item.title}</Typography>
                    </Box>
                </TableCell>
                <TableCell align="center">
                    <Chip
                        label={item.color}
                        size="small"
                        sx={{
                            bgcolor: alpha(item.color, 0.12),
                            color: item.color,
                            border: '1px solid',
                            borderColor: alpha(item.color, 0.3),
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            height: 24,
                            fontWeight: 600
                        }}
                    />
                </TableCell>
                <TableCell align="center">
                    <Tooltip title={item.isGlobal ? "Global - disponível em todos os quadros" : "Local - disponível apenas neste quadro"}>
                        <Chip
                            icon={item.isGlobal
                                ? <PublicIcon fontSize="small" sx={{ fontSize: 14, ml: '4px !important' }} />
                                : <PrivateIcon fontSize="small" sx={{ fontSize: 14, ml: '4px !important' }} />
                            }
                            label={item.isGlobal ? "Global" : "Local"}
                            size="small"
                            color={item.isGlobal ? "info" : "warning"}
                            variant="outlined"
                            sx={{
                                height: 24,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                '& .MuiChip-icon': {
                                    color: 'inherit'
                                }
                            }}
                        />
                    </Tooltip>
                </TableCell>
                <TableCell align="right">
                    {isLoading ? (
                        <CircularProgress size={20} thickness={4} />
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <ActionButton
                                icon={EditIcon}
                                tooltip="Editar"
                                onClick={() => handleEdit(item, itemType)}
                                disabled={loading || editingItem !== null}
                                ariaLabel={`Editar ${itemType === 'tag' ? 'marcador' : 'prioridade'}`}
                            />
                            <ActionButton
                                icon={DeleteIcon}
                                tooltip="Excluir"
                                onClick={() => handleDelete(item.id, itemType)}
                                disabled={loading || editingItem !== null}
                                color="error"
                                ariaLabel={`Excluir ${itemType === 'tag' ? 'marcador' : 'prioridade'}`}
                            />
                        </Box>
                    )}
                </TableCell>
            </TableRow>
        );
    };

    const TagsContent = () => (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={500}>
                    Marcadores
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => handleAddNew('tag')}
                    disabled={loading || editingItem !== null}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 1.5,
                        fontWeight: 500,
                        boxShadow: 'none'
                    }}
                >
                    Adicionar
                </Button>
            </Box>

            <Collapse in={showHelp}>
                <Alert
                    severity="info"
                    sx={{ mb: 3, borderRadius: 1.5 }}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setShowHelp(false)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    <Typography variant="body2">
                        <strong>Marcadores globais</strong> estão disponíveis em todos os quadros. <strong>Marcadores locais</strong> são específicos para este quadro.
                    </Typography>
                </Alert>
            </Collapse>

            <Collapse in={hasErrors}>
                <Alert
                    severity="error"
                    sx={{ mb: 3, borderRadius: 1.5 }}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setHasErrors(false)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    <Typography variant="body2">
                        {errorMessage}
                    </Typography>
                </Alert>
            </Collapse>

            <SearchFilterBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                sortConfig={sortConfig}
                handleSort={handleSort}
                filterGlobal={filterGlobal}
                handleFilterGlobalChange={handleFilterGlobalChange}
            />

            {searchQuery && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {filteredTags.length === 0
                        ? 'Nenhum marcador encontrado'
                        : `${filteredTags.length} ${filteredTags.length === 1 ? 'marcador encontrado' : 'marcadores encontrados'}`}
                </Typography>
            )}

            <DataTable
                items={filteredTags}
                itemType="tag"
                columns={[
                    { title: 'Nome' },
                    { title: 'Cor', align: 'center', width: 110 },
                    { title: 'Escopo', align: 'center', width: 80 },
                    { title: 'Ações', align: 'right', width: 100 }
                ]}
                renderRow={(tag) => <ItemTableRow item={tag} itemType="tag" />}
                emptyStateProps={{
                    icon: LabelIcon,
                    message: 'Nenhum marcador encontrado',
                    subMessage: searchQuery
                        ? 'Tente ajustar os filtros ou usar termos diferentes na pesquisa'
                        : 'Comece adicionando um novo marcador',
                    buttonText: 'Adicionar Marcador',
                    onButtonClick: () => handleAddNew('tag')
                }}
                isLoading={loading}
            />
        </Box>
    );

    const PrioritiesContent = () => (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={500}>
                    Prioridades
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => handleAddNew('priority')}
                    disabled={loading || editingItem !== null}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 1.5,
                        fontWeight: 500,
                        boxShadow: 'none'
                    }}
                >
                    Adicionar
                </Button>
            </Box>

            <Collapse in={showHelp}>
                <Alert
                    severity="info"
                    sx={{ mb: 3, borderRadius: 1.5 }}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setShowHelp(false)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    <Typography variant="body2">
                        <strong>Prioridades globais</strong> estão disponíveis em todos os quadros. <strong>Prioridades locais</strong> são específicas para este quadro.
                    </Typography>
                </Alert>
            </Collapse>

            <Collapse in={hasErrors}>
                <Alert
                    severity="error"
                    sx={{ mb: 3, borderRadius: 1.5 }}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setHasErrors(false)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    <Typography variant="body2">
                        {errorMessage}
                    </Typography>
                </Alert>
            </Collapse>

            <SearchFilterBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                sortConfig={sortConfig}
                handleSort={handleSort}
                filterGlobal={filterGlobal}
                handleFilterGlobalChange={handleFilterGlobalChange}
            />

            {searchQuery && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {filteredPriorities.length === 0
                        ? 'Nenhuma prioridade encontrada'
                        : `${filteredPriorities.length} ${filteredPriorities.length === 1 ? 'prioridade encontrada' : 'prioridades encontradas'}`}
                </Typography>
            )}

            <DataTable
                items={filteredPriorities}
                itemType="priority"
                columns={[
                    { title: 'Nome' },
                    { title: 'Cor', align: 'center', width: 110 },
                    { title: 'Escopo', align: 'center', width: 80 },
                    { title: 'Ações', align: 'right', width: 100 }
                ]}
                renderRow={(priority) => <ItemTableRow item={priority} itemType="priority" />}
                emptyStateProps={{
                    icon: PriorityIcon,
                    message: 'Nenhuma prioridade encontrada',
                    subMessage: searchQuery
                        ? 'Tente ajustar os filtros ou usar termos diferentes na pesquisa'
                        : 'Comece adicionando uma nova prioridade',
                    buttonText: 'Adicionar Prioridade',
                    onButtonClick: () => handleAddNew('priority')
                }}
                isLoading={loading}
            />
        </Box>
    );

    const UsersContent = () => (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={500}>
                    Usuários Vinculados
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={handleOpenAddUser}
                    disabled={loading}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 1.5,
                        fontWeight: 500,
                        boxShadow: 'none'
                    }}
                >
                    Adicionar Usuário
                </Button>
            </Box>

            <Collapse in={showHelp}>
                <Alert
                    severity="info"
                    sx={{ mb: 3, borderRadius: 1.5 }}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setShowHelp(false)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    <Typography variant="body2">
                        <strong>Administradores</strong> podem gerenciar o quadro, enquanto <strong>Editores</strong> podem apenas editar cards.
                    </Typography>
                </Alert>
            </Collapse>

            <Collapse in={hasErrors}>
                <Alert
                    severity="error"
                    sx={{ mb: 3, borderRadius: 1.5 }}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setHasErrors(false)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    <Typography variant="body2">
                        {errorMessage}
                    </Typography>
                </Alert>
            </Collapse>

            <SearchFilterBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                sortConfig={sortConfig}
                handleSort={handleSort}
                showGlobalFilter={false}
            />

            {searchQuery && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {filteredUsers.length === 0
                        ? 'Nenhum usuário encontrado'
                        : `${filteredUsers.length} ${filteredUsers.length === 1 ? 'usuário encontrado' : 'usuários encontrados'}`}
                </Typography>
            )}

            <DataTable
                items={filteredUsers}
                itemType="user"
                columns={[
                    { title: 'Usuário' },
                    { title: 'Tipo de Acesso', align: 'center', width: 150 },
                    { title: '', align: 'right', width: 60 }
                ]}
                renderRow={(user) => {
                    const isLoading = actionLoading.type && actionLoading.id === user.id;

                    return (
                        <TableRow key={user.id} hover>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar
                                        src={user.avatar}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            mr: 1.5,
                                            fontSize: '0.875rem',
                                            bgcolor: user.color || 'primary.main'
                                        }}
                                    >
                                        {!user.avatar && user.name.substring(0, 1).toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ID: {user.id}
                                        </Typography>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Box sx={{
                                        display: 'flex',
                                        borderRadius: 1,
                                        border: `1px solid ${theme.palette.divider}`,
                                        overflow: 'hidden',
                                        height: 32
                                    }}>
                                        <Button
                                            size="small"
                                            disableElevation
                                            onClick={() => handleChangeUserRole(user.id, 'admin')}
                                            disabled={isLoading}
                                            sx={{
                                                borderRadius: 0,
                                                minWidth: 'auto',
                                                px: 1.5,
                                                py: 0.5,
                                                textTransform: 'none',
                                                color: user.role === 'admin' ? theme.palette.primary.main : theme.palette.text.secondary,
                                                bgcolor: user.role === 'admin' ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                                '&:hover': {
                                                    bgcolor: user.role === 'admin'
                                                        ? alpha(theme.palette.primary.main, 0.12)
                                                        : alpha(theme.palette.action.hover, 0.08)
                                                }
                                            }}
                                        >
                                            <SettingsIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                                            Admin
                                        </Button>
                                        <Button
                                            size="small"
                                            disableElevation
                                            onClick={() => handleChangeUserRole(user.id, 'editor')}
                                            disabled={isLoading}
                                            sx={{
                                                borderRadius: 0,
                                                minWidth: 'auto',
                                                px: 1.5,
                                                py: 0.5,
                                                textTransform: 'none',
                                                color: user.role === 'editor' ? theme.palette.primary.main : theme.palette.text.secondary,
                                                bgcolor: user.role === 'editor' ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                                '&:hover': {
                                                    bgcolor: user.role === 'editor'
                                                        ? alpha(theme.palette.primary.main, 0.12)
                                                        : alpha(theme.palette.action.hover, 0.08)
                                                }
                                            }}
                                        >
                                            <EditIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                                            Editor
                                        </Button>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell align="right">
                                {isLoading ? (
                                    <CircularProgress size={20} thickness={4} />
                                ) : (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveUser(user.id)}
                                        sx={{
                                            color: theme.palette.error.main,
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.error.main, 0.08)
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" sx={{ fontSize: 18 }} />
                                    </IconButton>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                }}
                emptyStateProps={{
                    icon: PeopleIcon,
                    message: 'Nenhum usuário encontrado',
                    subMessage: searchQuery
                        ? 'Tente ajustar os filtros ou usar termos diferentes na pesquisa'
                        : 'Adicione usuários para colaborar neste quadro',
                    buttonText: 'Adicionar Usuário',
                    onButtonClick: handleOpenAddUser
                }}
                isLoading={loading}
            />
        </Box>
    );

    // Edit form for tags and priorities
    const EditForm = () => {
        if (!editingItem) return null;

        const isNewItem = !editingItem.id || (editingItem.id && !editingItem.title);
        const title = isNewItem
            ? `Adicionar ${editingItem.type === 'tag' ? 'Marcador' : 'Prioridade'}`
            : `Editar ${editingItem.type === 'tag' ? 'Marcador' : 'Prioridade'}`;

        const isSaving = actionLoading.type === 'add' || actionLoading.type === 'edit';

        return (
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton
                        size="small"
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        sx={{
                            mr: 2,
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.08)
                            }
                        }}
                    >
                        <ArrowForwardIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                    </IconButton>
                    <Typography variant="h6" fontWeight={500}>
                        {title}
                    </Typography>
                </Box>

                <Collapse in={hasErrors}>
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: 1.5 }}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => setHasErrors(false)}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        <Typography variant="body2">
                            {errorMessage}
                        </Typography>
                    </Alert>
                </Collapse>

                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 'none',
                        mb: 3,
                        bgcolor: alpha(theme.palette.background.paper, 0.7)
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            label="Nome"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={editingItem.title}
                            onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                            InputProps={{ sx: { borderRadius: 1.5 } }}
                            placeholder="Digite o nome"
                            autoFocus
                            disabled={isSaving}
                        />

                        <Box sx={{ position: 'relative' }}>
                            <MuiColorInput
                                format="hex"
                                label="Cor (Hex)"
                                name="color"
                                size="small"
                                disabled={isSaving}
                                value={colorPick}
                                inputRef={greetingRef}
                                onChange={handleColorChange}
                                variant="outlined"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                        }
                                    }
                                }}
                            />
                        </Box>

                        <ScopeSelector
                            value={editingItem.isGlobal}
                            onChange={(value) => setEditingItem({ ...editingItem, isGlobal: value })}
                            labelGlobal="Global"
                            labelLocal="Local"
                            descGlobal="Disponível em todos os quadros"
                            descLocal="Disponível apenas neste quadro"
                        />
                    </Box>
                </Paper>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 1.5,
                            px: 2,
                            fontWeight: 500
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={(e) => isNewItem ? handleSaveNew(e) : handleSaveEdit(e)}
                        disabled={isSaving || !editingItem?.title?.trim()}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 1.5,
                            px: 2,
                            boxShadow: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {isSaving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Box>
            </Box>
        );
    };

    // Add user dialog
    const AddUserDialog = () => (
        <Dialog
            open={addingUser}
            onClose={handleCloseAddUser}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    p: 1
                }
            }}
        >
            <DialogTitle>
                <Typography variant="h6" fontWeight={500}>
                    Adicionar Usuário
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                    <Autocomplete
                        options={filteredAllUsers}
                        getOptionLabel={(option) => `${option.name} (ID: ${option.id})`}
                        renderOption={(props, option) => (
                            <li {...props}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Avatar
                                        src={option.avatar}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            mr: 1.5,
                                            fontSize: '0.875rem',
                                            bgcolor: option.color || 'primary.main'
                                        }}
                                    >
                                        {!option.avatar && option.name.substring(0, 1).toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ID: {option.id}
                                        </Typography>
                                    </Box>
                                </Box>
                            </li>
                        )}
                        value={selectedUser}
                        onChange={(event, newValue) => {
                            setSelectedUser(newValue);
                        }}
                        inputValue={searchUserQuery}
                        onInputChange={(event, newInputValue) => {
                            setSearchUserQuery(newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Buscar usuário"
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                            </InputAdornment>
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                    sx: { borderRadius: 1.5 }
                                }}
                                placeholder="Busque por nome ou ID"
                            />
                        )}
                        noOptionsText="Nenhum usuário encontrado"
                        loading={loading}
                        loadingText="Buscando usuários..."
                        sx={{ width: '100%' }}
                    />

                    <ScopeSelector
                        value={userRole === 'admin'}
                        onChange={(value) => setUserRole(value ? 'admin' : 'editor')}
                        labelGlobal="Administrador"
                        labelLocal="Editor"
                        descGlobal="Pode gerenciar o quadro e suas configurações"
                        descLocal="Pode criar e editar cards no quadro"
                    />
                </Box>
            </DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, gap: 1.5 }}>
                <Button
                    variant="outlined"
                    onClick={handleCloseAddUser}
                    disabled={loading}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 1.5,
                        px: 2,
                        fontWeight: 500
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddUser}
                    disabled={loading || !selectedUser}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 1.5,
                        px: 2,
                        boxShadow: 'none',
                        fontWeight: 500,
                        '&:hover': {
                            boxShadow: 'none'
                        }
                    }}
                >
                    {loading ? 'Adicionando...' : 'Adicionar'}
                </Button>
            </Box>
        </Dialog>
    );

    // Sidebar tabs
    const sidebarTabs = [
        { id: 'display', label: 'Exibição', icon: SettingsIcon },
        { id: 'tags', label: 'Marcadores', icon: LabelIcon },
        { id: 'priorities', label: 'Prioridades', icon: PriorityIcon },
        { id: 'users', label: 'Vinculados (Em breve)', icon: PeopleIcon }
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    height: '80vh',
                    maxHeight: 700,
                    display: 'flex',
                    m: 0,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 8px 32px 0 rgba(0,0,0,0.5)'
                        : '0 8px 32px 0 rgba(0,0,0,0.1)'
                }
            }}
            TransitionProps={{ timeout: 200 }}
        >
            <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
                {/* Sidebar */}
                <Box
                    component="aside"
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        bgcolor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.paper, 0.6)
                            : alpha(theme.palette.grey[50], 0.8),
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{
                        p: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}>
                        <Typography variant="h6" fontWeight={600} color="primary.main">
                            Configurações
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={onClose}
                            aria-label="Fechar"
                            sx={{
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                                }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <List
                        component="nav"
                        sx={{
                            pt: 1.5,
                            px: 1,
                            flexGrow: 1
                        }}
                    >
                        {sidebarTabs.map((tab) => (
                            <ListItemButton
                                key={tab.id}
                                selected={activeTab === tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                disabled={tab.id === 'users'}
                                sx={{
                                    borderRadius: 1.5,
                                    mb: 0.5,
                                    '&.Mui-selected': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.primary.main, 0.2)
                                            : alpha(theme.palette.primary.main, 0.08),
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.primary.main, 0.3)
                                                : alpha(theme.palette.primary.main, 0.12),
                                        }
                                    }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <tab.icon fontSize="small" color={activeTab === tab.id ? 'primary' : 'action'} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={tab.label}
                                    primaryTypographyProps={{
                                        variant: 'body2',
                                        fontWeight: activeTab === tab.id ? 600 : 400,
                                        color: activeTab === tab.id ? 'primary' : 'text.primary'
                                    }}
                                />
                                {activeTab === tab.id && (
                                    <ArrowForwardIcon
                                        fontSize="small"
                                        color="primary"
                                        sx={{ opacity: 0.7, fontSize: 16 }}
                                    />
                                )}
                            </ListItemButton>
                        ))}
                    </List>

                    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography
                            variant="caption"
                            component="div"
                            color="text.secondary"
                            align="center"
                        >
                            {activeTab === 'tags' && `${tags.length} marcadores`}
                            {activeTab === 'priorities' && `${priorities.length} prioridades`}
                            {activeTab === 'users' && `${users.length} usuários vinculados`}
                        </Typography>
                    </Box>
                </Box>

                {/* Content Area */}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        bgcolor: theme.palette.background.paper
                    }}
                >
                    {editingItem ? (
                        <EditForm />
                    ) : (
                        <>
                            {activeTab === 'display' && <DisplaySettingsContent />}
                            {activeTab === 'tags' && <TagsContent />}
                            {activeTab === 'priorities' && <PrioritiesContent />}
                            {activeTab === 'users' && <UsersContent />}
                        </>
                    )}
                </Box>
            </Box>

            <AddUserDialog />
        </Dialog>
    );
};

export default CreateOrEditKanbanSettingsModal;